import { z } from 'zod';

import appData from './app.json';
import feedsData from './feeds.json';
import linksData from './links.json';
import musicData from './music.json';
import profileData from './profile.json';
import searchData from './search.json';
import siteData from './site.json';
import themeData from './theme.json';
import booksRoomData from './room-data/books.json';
import friendsRoomData from './room-data/friends.json';

export { sceneFont } from './fonts';

const httpsUrl = z.url().refine((value) => new URL(value).protocol === 'https:');
const localAsset = z.string().regex(/^\/assets\//);
const roomAsset = z.string().nullable();
const roomHttpUrl = z.string().refine((value) => {
  try {
    const protocol = new URL(value).protocol;
    return protocol === 'http:' || protocol === 'https:';
  } catch {
    return false;
  }
});

const appSchema = z.object({
  defaultCameraZone: z.enum(['overview', 'workspace', 'lounge']),
  name: z.string().min(1),
  owner: z.string().min(1),
});

const linkSchema = z.object({
  ctaLabel: z.string().min(1),
  description: z.string().min(1),
  id: z.string().regex(/^[a-z\d](?:[a-z\d-]*[a-z\d])?$/),
  tags: z.array(z.string().min(1)),
  title: z.string().min(1),
  url: httpsUrl,
});

const profileSchema = z.object({
  avatar: localAsset,
  bio: z.string(),
  experience: z.array(
    z.object({
      organization: z.string().min(1),
      period: z.string().min(1),
      role: z.string().min(1),
      summary: z.string().min(1),
    }),
  ),
  github: z.object({ username: z.string().min(1) }),
  intro: z.object({
    dollFonts: z
      .array(z.object({ family: z.string().min(1), src: localAsset }))
      .min(1)
      .optional()
      .default([
        { family: 'Ark Pixel', src: '/assets/fonts/ark-pixel-12px-proportional-zh-cn.woff' },
      ]),
    role: z.string().min(1),
  }),
  name: z.string().min(1),
  skills: z.array(
    z.object({
      label: z.string().min(1),
      items: z.array(
        z.object({
          color: z.string().optional(),
          icon: z.string().min(1),
          level: z.enum(['low', 'medium', 'high', 'xhigh', 'max', 'ultra']),
          name: z.string().min(1),
        }),
      ),
    }),
  ),
  socialLinks: z.array(
    z.object({ color: z.string().optional(), label: z.string(), url: httpsUrl }),
  ),
});

const feedSchema = z.object({
  avatar: httpsUrl.optional(),
  defaultFetch: z.boolean(),
  enabled: z.boolean(),
  feedUrl: httpsUrl,
  id: z.string().regex(/^[a-z\d](?:[a-z\d-]*[a-z\d])?$/),
  name: z.string().min(1),
  siteUrl: httpsUrl,
  tags: z.array(z.string().min(1)),
});

const musicSchema = z.object({
  blog: z.object({ api: httpsUrl }),
  local: z.object({
    playlist: z.array(
      z.object({
        artist: z.string(),
        cover: localAsset.optional(),
        lrc: z.string(),
        name: z.string(),
        url: localAsset,
      }),
    ),
  }),
  mode: z.enum(['blog', 'local']),
  playMode: z.enum(['list', 'one', 'random']),
  showLyrics: z.boolean(),
  volume: z.number().min(0).max(1),
});

const commandItem = z.object({
  command: z.enum(['overview', 'open-profile', 'focus-bookshelf', 'open-music']),
  icon: z.string().optional(),
  id: z.string(),
  kind: z.literal('command'),
  label: z.string(),
});
const externalItem = z.object({
  icon: z.string().optional(),
  id: z.string(),
  kind: z.literal('external'),
  label: z.string(),
  searchTemplate: httpsUrl.optional(),
  url: httpsUrl,
});
const searchSchema = z.object({
  scopes: z.array(
    z.object({
      icon: z.string(),
      id: z.string(),
      items: z.array(z.discriminatedUnion('kind', [commandItem, externalItem])),
      label: z.string(),
      placeholder: z.string(),
    }),
  ),
});

const siteSchema = z.object({
  author: z.string().min(1),
  copyright: z.string().min(1),
  description: z.string().min(1),
  image: localAsset,
  siteName: z.string().min(1),
  socialDescription: z.string().min(1),
  title: z.string().min(1),
});

const themeSchema = z.object({
  defaultMode: z.enum(['light', 'dark']),
  soundStorageKey: z.string(),
  themeStorageKey: z.string(),
});

const bookSchema = z.object({
  id: z.string().min(1),
  title: z.string().min(1),
  cover: roomAsset,
  description: z.string(),
  status: z.number().nullable(),
  score: z.number().nullable(),
  tags: z.array(z.string()),
  detailPath: z.string().min(1),
});

const friendSchema = z.object({
  id: z.string().min(1),
  title: z.string().min(1),
  image: roomAsset,
  description: z.string(),
  url: roomHttpUrl,
  tags: z.array(z.string()),
});

const roomDataEnvelope = <T extends z.ZodType>(itemSchema: T) =>
  z.object({ version: z.literal(1), generatedAt: z.string(), items: z.array(itemSchema) });

export const appConfig = appSchema.parse(appData);
export const feedsConfig = z.array(feedSchema).parse(feedsData);
export const linksConfig = z.array(linkSchema).parse(linksData);
export const musicConfig = musicSchema.parse(musicData);
export const profileConfig = profileSchema.parse(profileData);
export const searchConfig = searchSchema.parse(searchData);
export const siteConfig = siteSchema.parse(siteData);
export const themeConfig = themeSchema.parse(themeData);
export const roomBooks = roomDataEnvelope(bookSchema).parse(booksRoomData).items;
export const roomFriends = roomDataEnvelope(friendSchema).parse(friendsRoomData).items;

export type FeedConfig = z.infer<typeof feedSchema>;
export type LinkConfig = z.infer<typeof linkSchema>;
export type MusicConfig = z.infer<typeof musicSchema>;
export type ProfileConfig = z.infer<typeof profileSchema>;
export type SearchConfig = z.infer<typeof searchSchema>;
export type SiteConfig = z.infer<typeof siteSchema>;
export type BookConfig = z.infer<typeof bookSchema>;
export type FriendConfig = z.infer<typeof friendSchema>;
